import classNames from 'classnames';
import { useId, useState, type ReactNode } from 'react';
import { Button } from '~/components/buttons/Button/Button';
import { FormDialog } from '~/components/dialogs/FormDialog';
import { PLATFORM_PARTICIPATION_AGREEMENT, TERMS_DOCUMENT } from './platformParticipationAgreement';

type Props = {
  open: boolean;
  /** Short lead-in shown above the agreement, e.g. what accepting does here. */
  description?: ReactNode;
  /** Label of the accept button (disabled until the box is checked). */
  primaryLabel: string;
  /** Label of the other button: "Cancel" where the user can back out, "Return to ..." where not. */
  secondaryLabel: string;
  /**
   * Disables the accept button and the box while a request is in flight. The
   * secondary button stays live: "Return to MedCredVault" must never be stuck
   * behind a save that does not finish.
   */
  busy?: boolean;
  /**
   * Whether Escape and a backdrop click act like the secondary button. A
   * required agreement (the HLX gate) sets this false so a stray key press
   * cannot dismiss it or navigate away.
   */
  dismissible?: boolean;
  onAccept: () => void;
  onSecondary: () => void;
};

const AgreementText = () => (
  <div
    // Focusable so keyboard users can scroll a long document.
    tabIndex={0}
    role="region"
    aria-label={`${PLATFORM_PARTICIPATION_AGREEMENT.title} text`}
    data-cy="platform-agreement-text"
    className="max-h-[45vh] overflow-y-auto rounded-lg border border-grey-900 p-4 text-left text-sm text-grey-300"
  >
    <p className="font-semibold text-grey-100">{PLATFORM_PARTICIPATION_AGREEMENT.company}</p>
    <p className="font-semibold uppercase text-grey-100">
      {PLATFORM_PARTICIPATION_AGREEMENT.title}
    </p>
    <p className="mb-3">
      {`Effective Date: ${PLATFORM_PARTICIPATION_AGREEMENT.effectiveDateLabel}`}
      <br />
      {`Version: ${PLATFORM_PARTICIPATION_AGREEMENT.version}`}
    </p>
    {PLATFORM_PARTICIPATION_AGREEMENT.sections.map((section, index) => (
      <section key={section.heading || `preamble-${index}`} className="mb-4 space-y-2">
        {section.heading && <h3 className="font-semibold text-grey-100">{section.heading}</h3>}
        {section.paragraphs.map((paragraph, paragraphIndex) => (
          // The text uses soft line breaks for its (i)...(xiv) lists and contact lines.
          <p key={paragraphIndex} className="whitespace-pre-line">
            {paragraph}
          </p>
        ))}
      </section>
    ))}
  </div>
);

const AgreementBody = ({
  description,
  primaryLabel,
  secondaryLabel,
  busy = false,
  onAccept,
  onSecondary,
}: Omit<Props, 'open' | 'dismissible'>) => {
  const [agreed, setAgreed] = useState(false);
  const checkboxId = useId();
  return (
    <div className="flex flex-col gap-4" data-cy="platform-agreement-dialog">
      {description && <div className="text-left text-sm text-grey-300">{description}</div>}
      <AgreementText />
      <label htmlFor={checkboxId} className="flex items-start gap-3 text-left text-sm">
        <input
          id={checkboxId}
          type="checkbox"
          data-cy="platform-agreement-checkbox"
          checked={agreed}
          disabled={busy}
          onChange={(event) => setAgreed(event.target.checked)}
          className={classNames(
            'mt-0.5 size-5 shrink-0 cursor-pointer rounded-md bg-grey-900',
            'border border-grey-800 accent-primary-50',
            'focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary-50',
            'disabled:cursor-not-allowed disabled:opacity-60'
          )}
        />
        <span className="text-grey-100">
          {`I have read and agree to the Platform Participation Agreement (version ${TERMS_DOCUMENT.version})`}
        </span>
      </label>
      <div className="flex flex-wrap justify-end gap-3">
        <Button
          type="button"
          colorVariant="tertiary"
          cyTag="platform-agreement-secondary"
          onClick={onSecondary}
        >
          {secondaryLabel}
        </Button>
        <Button
          type="button"
          colorVariant="primary"
          cyTag="platform-agreement-accept"
          disabled={!agreed || busy}
          onClick={onAccept}
        >
          {primaryLabel}
        </Button>
      </div>
    </div>
  );
};

/**
 * [MCV-1815][MCV-1755] The Platform Participation Agreement with a required
 * checkbox. The caller owns what accepting does (record it, or send it with an
 * unmask request or approval); this component only gathers the assent. The box
 * resets each time the dialog opens.
 */
export const PlatformAgreementDialog = ({ open, dismissible = true, ...body }: Props) => (
  <FormDialog
    editing={open}
    title={PLATFORM_PARTICIPATION_AGREEMENT.title}
    width="3xl"
    hideCloseButton={!dismissible}
    onClose={() => {
      if (dismissible && !body.busy) body.onSecondary();
    }}
  >
    <AgreementBody {...body} />
  </FormDialog>
);
